import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import {
  ArrowLeft,
  Crown,
  Mic,
  Check,
  AlertCircle,
  Loader2,
  Quote,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const SUPPORT_EMAIL = "support@podcastsabbreviated.com";
const FOUNDER_NAME = "Christopher Meade";

const CADENCE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "irregular", label: "Irregular / seasonal" },
];

interface ProgramStatus {
  approvedCount: number;
  cap: number;
  isFull: boolean;
}

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; isWaitlist: boolean }
  | { kind: "error"; message: string };

export default function FoundingPage() {
  // Add noindex,nofollow at runtime so this unlisted page never gets crawled,
  // and remove the tag on unmount so other pages aren't affected.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Founding Podcaster Program · Podcasts Abbreviated";
    return () => {
      meta.remove();
      document.title = prevTitle;
    };
  }, []);

  const [status, setStatus] = useState<ProgramStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${basePath}/api/founding/status`);
        if (!res.ok) return;
        const data = (await res.json()) as ProgramStatus;
        if (!cancelled) setStatus(data);
      } catch {
        // Silently ignore — page falls back to "open spots" copy.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const programIsFull = status?.isFull ?? false;

  return (
    <div className="min-h-[100dvh] bg-background text-foreground flex flex-col">
      <FoundingHeader />
      <main className="flex-1">
        <Hero programIsFull={programIsFull} status={status} />
        <WhatsIncluded />
        <ExchangeAndFit />
        <HonestPitch />
        <ApplicationForm programIsFull={programIsFull} />
        <FAQ />
      </main>
      <FoundingFooter />
    </div>
  );
}

// ── Header ──────────────────────────────────────────────────────────────────

function FoundingHeader() {
  return (
    <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2 group">
          <img
            src={`${basePath}/pa-icon.jpg`}
            alt="Podcasts Abbreviated"
            className="h-8 w-8 rounded-sm object-cover group-hover:scale-105 transition-transform"
          />
          <span className="font-mono font-bold tracking-tight uppercase text-foreground">
            Podcasts <span className="text-primary">Abbreviated</span>
          </span>
        </Link>
        <Link
          href="/"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors font-mono uppercase tracking-wider"
        >
          <ArrowLeft size={13} /> Back to home
        </Link>
      </div>
    </header>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────────

function Hero({
  programIsFull,
  status,
}: {
  programIsFull: boolean;
  status: ProgramStatus | null;
}) {
  const handleApply = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section className="relative overflow-hidden border-b border-border/50">
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />
      <div className="max-w-4xl mx-auto px-4 py-16 sm:py-24 relative">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary font-mono uppercase text-[10px] tracking-wider mb-6">
          <Crown size={12} /> Invite-only · 10 spots
        </div>
        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-5">
          Get 6 months of Pro <span className="text-primary">free</span>. Help us build this right.
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8">
          Six months of Pro on the house, in exchange for your honest feedback.
          We're picking{" "}
          <span className="text-foreground font-semibold">10 podcasters</span>{" "}
          to help shape what Podcasts Abbreviated becomes. No credit card. No
          catch.
        </p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {programIsFull ? (
            <a href="#apply" onClick={handleApply}>
              <Button size="lg" variant="outline" className="font-mono uppercase tracking-wider">
                Join the waitlist
              </Button>
            </a>
          ) : (
            <a href="#apply" onClick={handleApply}>
              <Button size="lg" className="font-mono uppercase tracking-wider">
                Apply now <Mic size={14} className="ml-2" />
              </Button>
            </a>
          )}
          {status && (
            <span className="text-xs font-mono text-muted-foreground">
              {programIsFull
                ? `All ${status.cap} founding spots are full. Applications join the waitlist.`
                : `${status.cap - status.approvedCount} of ${status.cap} spots remaining`}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Section helpers ─────────────────────────────────────────────────────────

function SectionHeading({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="mb-10">
      <div className="text-xs font-mono uppercase tracking-wider text-primary mb-3">
        {eyebrow}
      </div>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">{title}</h2>
      {subtitle && <p className="text-muted-foreground max-w-2xl">{subtitle}</p>}
    </div>
  );
}

function Section({
  id,
  children,
  className = "",
}: {
  id?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`border-b border-border/50 ${className}`}>
      <div className="max-w-4xl mx-auto px-4 py-16 sm:py-20">{children}</div>
    </section>
  );
}

// ── What's included ─────────────────────────────────────────────────────────

const INCLUDED = [
  {
    icon: <Crown size={18} />,
    title: "6 months of Pro, free",
    body:
      "Full access to every Pro feature for half a year. Worth roughly the price of a few coffees a month. Yours, on us.",
  },
  {
    icon: <Mic size={18} />,
    title: "Direct line to the founder",
    body:
      "Email, DM, or voice memo. Your feedback skips the queue and goes straight to the person who can actually act on it.",
  },
  {
    icon: <Quote size={18} />,
    title: "Founding podcaster pricing",
    body:
      "A permanent discount on Pro when we launch publicly. You also get an optional shout-out on our launch page (opt out any time).",
  },
];

function WhatsIncluded() {
  return (
    <Section>
      <SectionHeading
        eyebrow="What you get"
        title="What's included"
        subtitle="The honest version: real software, real access, no marketing fluff."
      />
      <div className="grid sm:grid-cols-3 gap-4">
        {INCLUDED.map((item) => (
          <Card key={item.title} className="border-border/60 bg-card/40">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="shrink-0 h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </Section>
  );
}

// ── Exchange and fit ─────────────────────────────────────────────────────────

const EXCHANGE_BULLETS = [
  "Use it on at least one real episode. We need to know how it performs in your actual workflow, not a test run.",
  "Reply when we ask you a question. Short answers are fine. We are not going to spam you.",
  "Tell us what is confusing, broken, or missing. Especially the awkward stuff.",
  "If you love it, mention us. If you hate it, tell us why first.",
  "You publish on a regular cadence. Weekly, biweekly, or monthly works. You have a handful of episodes already out.",
  "You have opinions about your tools. You know what you want and you say so when something does not fit.",
  "You are curious about AI in podcasting. You want to see how it can fit into your workflow without replacing the human parts.",
];

function ExchangeAndFit() {
  return (
    <Section>
      <SectionHeading
        eyebrow="The exchange and the fit"
        title="What we're asking for, and who this is for"
        subtitle="Nothing financial. Just real use, real feedback, and a podcaster who actually fits."
      />
      <Card className="border-border/60 bg-card/40">
        <CardContent className="p-6">
          <ul className="space-y-3">
            {EXCHANGE_BULLETS.map((bullet) => (
              <li key={bullet} className="flex items-start gap-3">
                <Check size={16} className="text-primary mt-1 shrink-0" />
                <span className="text-sm text-foreground/90 leading-relaxed">{bullet}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
            That is it. No required hours. No NDA. No exclusivity. If at any point this
            stops being worth your time, just tell us and we part as friends.
          </p>
        </CardContent>
      </Card>
    </Section>
  );
}

// ── The honest pitch ────────────────────────────────────────────────────────

function HonestPitch() {
  return (
    <Section>
      <SectionHeading eyebrow="The real reason" title="The honest pitch" />
      <Card className="border-border/60 bg-card/40">
        <CardContent className="p-6 sm:p-8">
          <div className="prose prose-invert max-w-none text-foreground/90">
            <p className="leading-relaxed mb-4">
              We could put up a "request a demo" form and bury this behind a sales
              call. We're not going to do that.
            </p>
            <p className="leading-relaxed mb-4">
              The truth is, we're early. The product works. Really well, for what it
              does. But the next version of it depends on which podcasters actually
              show up and start using it on real episodes. The features we'll build
              next, the ones we'll cut, the rough edges we'll smooth: those decisions
              get a lot easier when ten thoughtful creators are in the loop.
            </p>
            <p className="leading-relaxed mb-4">
              In exchange for showing up, we're giving you the most valuable thing we
              can right now: six months of full access, on us, with a real human on
              the other end of every email. No credit card. No automatic billing
              when the six months end. If you want to keep going, you'll choose to.
            </p>
            <p className="leading-relaxed mb-0 text-muted-foreground">
              That's the deal. If it sounds fair, please apply below.
            </p>
          </div>
        </CardContent>
      </Card>
    </Section>
  );
}

// ── Application form ────────────────────────────────────────────────────────

function ApplicationForm({ programIsFull }: { programIsFull: boolean }) {
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: "idle" });
  const formRef = useRef<HTMLFormElement>(null);
  const [cadence, setCadence] = useState<string>("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitState.kind === "submitting") return;

    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      name: String(fd.get("name") ?? "").trim(),
      email: String(fd.get("email") ?? "").trim(),
      podcast: String(fd.get("podcast") ?? "").trim(),
      cadence,
      reason: String(fd.get("reason") ?? "").trim(),
    };

    // Waitlist mode collapses the form to name + email only. Skip the
    // podcast/cadence checks in that case so the slim form can submit.
    if (programIsFull) {
      if (!payload.name || !payload.email) {
        setSubmitState({ kind: "error", message: "Please enter your name and email." });
        return;
      }
    } else if (!payload.name || !payload.email || !payload.podcast || !payload.cadence) {
      setSubmitState({ kind: "error", message: "Please fill in all required fields." });
      return;
    }

    setSubmitState({ kind: "submitting" });
    try {
      const res = await fetch(`${basePath}/api/founding/applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitState({
          kind: "error",
          message: typeof data?.error === "string"
            ? data.error
            : "Something went wrong. Please try again in a minute.",
        });
        return;
      }
      setSubmitState({ kind: "success", isWaitlist: Boolean(data?.isWaitlist) });
      form.reset();
      setCadence("");
    } catch {
      setSubmitState({
        kind: "error",
        message: "Couldn't reach the server. Check your connection and try again.",
      });
    }
  }

  return (
    <Section id="apply">
      <SectionHeading
        eyebrow={programIsFull ? "Waitlist" : "Apply"}
        title={programIsFull ? "Join the waitlist" : "Apply for a founding spot"}
        subtitle={
          programIsFull
            ? "All 10 founding spots are filled, but if anything opens up we'll come back to the waitlist first."
            : "Takes about two minutes. We read every application personally and reply within a couple of days."
        }
      />

      {submitState.kind === "success" ? (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="shrink-0 h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                <Check size={20} />
              </div>
              <div>
                {/* Per-spec confirmation copy: "Got it. Talk soon." */}
                <h3 className="font-semibold text-lg mb-2">Got it. Talk soon.</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {submitState.isWaitlist
                    ? "You're on the waitlist. The first 10 founding spots are full, but if a slot opens up you'll be the first to hear from us. Check your inbox for a confirmation email."
                    : (
                      <>
                        Thanks for applying. You'll hear back within a couple of days, either way. Check your inbox for a confirmation, or write to us directly at{" "}
                        {/* TODO: replace with real support email */}
                        <a href="mailto:support@podcastsabbreviated.com" className="text-primary hover:underline">
                          support@podcastsabbreviated.com
                        </a>
                        .
                      </>
                    )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/60 bg-card/40">
          <CardContent className="p-6 sm:p-8">
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="founding-name" className="font-mono uppercase text-[11px] tracking-wider text-muted-foreground mb-2 block">
                    Your name *
                  </Label>
                  <Input
                    id="founding-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    maxLength={200}
                    placeholder="Alex Rivera"
                  />
                </div>
                <div>
                  <Label htmlFor="founding-email" className="font-mono uppercase text-[11px] tracking-wider text-muted-foreground mb-2 block">
                    Email *
                  </Label>
                  <Input
                    id="founding-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={320}
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              {/*
                Waitlist mode (programIsFull === true): collapse to name+email.
                Once spots are full, we just collect contact info so we can
                reach out if a slot opens up — no point asking for podcast
                details we won't act on.
              */}
              {!programIsFull && (
                <>
                  <div>
                    <Label htmlFor="founding-podcast" className="font-mono uppercase text-[11px] tracking-wider text-muted-foreground mb-2 block">
                      Podcast name and/or RSS URL *
                    </Label>
                    <Input
                      id="founding-podcast"
                      name="podcast"
                      type="text"
                      required
                      maxLength={500}
                      placeholder="The Indie Pod, https://example.com/feed.xml"
                    />
                    <p className="text-xs text-muted-foreground mt-1.5">
                      An RSS link helps us understand the show, but isn't required.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="founding-cadence" className="font-mono uppercase text-[11px] tracking-wider text-muted-foreground mb-2 block">
                      Publishing cadence *
                    </Label>
                    <Select value={cadence} onValueChange={setCadence}>
                      <SelectTrigger id="founding-cadence">
                        <SelectValue placeholder="How often do you publish?" />
                      </SelectTrigger>
                      <SelectContent>
                        {CADENCE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="founding-reason" className="font-mono uppercase text-[11px] tracking-wider text-muted-foreground mb-2 block">
                      Why are you interested? <span className="text-muted-foreground/60 normal-case tracking-normal text-xs">(optional)</span>
                    </Label>
                    <Textarea
                      id="founding-reason"
                      name="reason"
                      rows={5}
                      maxLength={4000}
                      placeholder="Anything that helps us understand your show or what you'd want from a tool like this. A sentence or two is fine."
                    />
                  </div>
                </>
              )}

              {submitState.kind === "error" && (
                <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{submitState.message}</span>
                </div>
              )}

              <div className="flex items-center justify-between gap-4 flex-wrap pt-2">
                <p className="text-xs text-muted-foreground">
                  No credit card required. We'll only email you about your application.
                </p>
                <Button
                  type="submit"
                  disabled={submitState.kind === "submitting"}
                  className="font-mono uppercase tracking-wider"
                >
                  {submitState.kind === "submitting" ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" /> Sending…
                    </>
                  ) : programIsFull ? (
                    <>Join waitlist <MessageSquare size={14} className="ml-2" /></>
                  ) : (
                    <>Submit application <Mic size={14} className="ml-2" /></>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </Section>
  );
}

// ── FAQ ─────────────────────────────────────────────────────────────────────

const FAQS = [
  {
    q: "What happens after the 6 months end?",
    a: `You decide. We don't take a credit card up front, so nothing auto-charges. When your 6 months are up we'll send a heads-up; if you want to keep going on a paid plan you'll choose to. Otherwise you drop down to the free tier — your account, history, and saved settings stay put.`,
  },
  {
    q: "Will my podcast be made public anywhere?",
    a: "Only if you want it to be. We may invite founding podcasters to be listed on our launch page when we open to the public, but it's opt-in and you can decline or change your mind any time.",
  },
  {
    q: "How is this different from a free trial?",
    a: "A trial is a sales tactic. We're trying to get you to convert. This isn't that. We're offering a real chunk of free time in exchange for real feedback, and we'd rather have an honest 'this isn't for me' than a polite paying customer who never opens the app.",
  },
  {
    q: "Do I have to be on a particular network or have a minimum download count?",
    a: "No. We care more about how thoughtfully you'll engage than how big your show is. A 50-listener show with a clear creator voice is exactly the kind of feedback we want.",
  },
  {
    q: "How do I get the 6 months once I'm accepted?",
    a: "We'll send an approval email with a sign-up link. Whether you make an account before or after we approve you, the 6 months of Pro shows up automatically when your email matches the application. No codes, no support tickets.",
  },
  {
    q: "Can I refer a friend?",
    a: `Please do, but spots are limited to 10, so we can only take a few each round. If you have someone in mind, point them to this page or send their info to ${SUPPORT_EMAIL}.`,
  },
];

function FAQ() {
  return (
    <Section className="border-b-0">
      <SectionHeading eyebrow="FAQ" title="Frequently asked" />
      <Card className="border-border/60 bg-card/40">
        <CardContent className="p-2 sm:p-4">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((item, idx) => (
              <AccordionItem
                key={item.q}
                value={`faq-${idx}`}
                className={idx === FAQS.length - 1 ? "border-b-0" : ""}
              >
                <AccordionTrigger className="text-left font-medium px-3">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed px-3">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>
    </Section>
  );
}

// ── Footer ──────────────────────────────────────────────────────────────────

function FoundingFooter() {
  return (
    <footer className="border-t border-border/50">
      <div className="max-w-4xl mx-auto px-4 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono text-muted-foreground">
        <div>© Podcasts Abbreviated</div>
        <div className="flex items-center gap-4">
          <Link href="/privacy" className="hover:text-foreground transition-colors">
            Privacy
          </Link>
          <Link href="/terms" className="hover:text-foreground transition-colors">
            Terms
          </Link>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-foreground transition-colors">
            Contact
          </a>
        </div>
      </div>
      {/* Hidden marker so dev/QA can verify the founder name placeholder is wired. */}
      <span className="sr-only">{FOUNDER_NAME}</span>
    </footer>
  );
}
